/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, tap } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { LocalStorageService } from './local-storage.service';
import { User } from '../../shared/models/user.model';
import { environment } from '../../../environments/environment';
//import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  loggedIn: boolean = false;
  currentUser: User = new User();
  isAdmin: boolean = false;
  isTeamAdmin: boolean = false;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  userSubscription = signal<any>(null);
  httpHeaders = new HttpHeaders().set('Content-Type', 'application/json');
  userLoggedIn: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  userLoggedOut: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private router: Router,
    private jwtHelper: JwtHelperService,
    private storage: LocalStorageService,
  ) {
    // this.initializeAuthState();
  }

  initializeAuthState() {
    const token = this.storage.getItem('token');
    if (token && !this.isTokenExpired(token)) {
      const decodedUser = this.decodeUserToken(token);
      this.setCurrentUser(decodedUser);
      this.loggedIn = true;
      this.userLoggedIn.next(true);
    }
  }

  loginWithEmail(email: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const options = {
      email: email,
    };
    const API = `${this.apiUrl}/api/auth/login`;
    return this.http.post(API, options, { headers: headers });
  }

  signUp(formData: {
    email: string;
    firstName: string;
    lastName: string;
  }): Observable<any> {
    const API = `${this.apiUrl}/api/auth/signup`;
    return this.http.post(API, formData);
  }

  verifyLogin(email: string, code: string) {
    const API = `${this.apiUrl}/api/auth/login/verify/${email}/${code}`;
    return this.http.get(API);
  }

  decodeUserToken(token: string) {
    return this.jwtHelper.decodeToken(token).usr;
  }

  setCurrentUser(decodedUser: User) {
    if (decodedUser) {
      this.storage.setItem(
        'uid',
        JSON.stringify({ email: decodedUser.email, id: decodedUser._id }),
      );
      this.currentUser._id = decodedUser._id;
      this.currentUser.role = decodedUser.role;
      this.currentUser.displayName = decodedUser.firstName
        ? decodedUser.firstName + ' ' + decodedUser.lastName
        : decodedUser.email;
      this.currentUser.firstName = decodedUser.firstName;
      this.currentUser.lastName = decodedUser.lastName;
      this.currentUser.email = decodedUser.email;
      this.currentUser.onboard = decodedUser.onboard;
      this.currentUser.status = decodedUser.status;
      this.currentUser.isCorporate = decodedUser.isCorporate;
      this.currentUser.profilePic = decodedUser.profilePic;
      this.currentUser.organization = decodedUser.organization;
      this.currentUser.rzPlanId = decodedUser.rzPlanId;
      this.currentUser.rzCustomerId = decodedUser.rzCustomerId;
      this.currentUser.rzSubscriptionId = decodedUser.rzSubscriptionId;
      this.currentUser.isTeamAdmin = decodedUser.isTeamAdmin;
      this.currentUser.teamSize = decodedUser.teamSize;
      decodedUser.role == 'admin'
        ? (this.isAdmin = true)
        : (this.isAdmin = false);
      decodedUser.role == 'manager'
        ? (this.isTeamAdmin = true)
        : (this.isTeamAdmin = false);
      this.currentUserSubject.next(this.currentUser);
    }
  }

  getCurrentUser() {
    return this.currentUserSubject.asObservable();
  }

  getTokenExpirationDate(token: string): Date | null {
    const decoded = this.jwtHelper.decodeToken(token);
    if (decoded.exp === undefined) {
      return null;
    }
    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  isTokenExpired(token?: string | null): boolean {
    if (!token) {
      token = this.storage.getItem('token');
    }
    if (!token) {
      return true;
    }
    const date = this.getTokenExpirationDate(token);
    if (date === undefined) {
      return false;
    }
    return !(date && date?.valueOf() > new Date().valueOf());
  }

  isLoggedIn() {
    if (!this.isTokenExpired() && this.loggedIn) {
      return true;
    } else {
      return false;
    }
  }

  loginUser(token: string) {
    this.storage.setItem('token', token); // Store token in local storage
    const decodedUser = this.decodeUserToken(token);
    this.setCurrentUser(decodedUser);
    this.loggedIn = true;
    this.userLoggedIn.next(true);
    this.fetchUserSubscription(decodedUser._id);
    this.router.navigate(['/layout/']);
  }

  logout() {
    this.storage.removeItem('token');
    this.storage.removeItem('uid');
    this.loggedIn = false;
    this.currentUser = new User();
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  ssoLogin(token: string) {
    this.storage.setItem('token', token);
    const decodedUser = this.decodeUserToken(token);
    this.setCurrentUser(decodedUser);
    this.loggedIn = true;
    this.router.navigate(['/dashboard']);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSniperLink(recipient: any) {
    const API = `https://sniperl.ink/v1/render?recipient=${recipient}&sender=autobazaar@katalysttech.com`;
    return this.http.get(API);
  }

  getTimezones() {
    const API = `${this.apiUrl}/api/mailbox/timezones`;
    return this.http.get(API);
  }

  getUserProfile() {
    const url = `${this.apiUrl}/api/user/profile/me`;
    return this.http.get(url);
  }

  signOut() {
    const keysToRemove = [
      '_showSidebar',
      'emailPreferencesCompleted',
      'emailServerCompleted',
      'onboard',
      'otherSavedSuccessfully',
      'rzp_checkout_anon_id',
      'rzp_device_id',
      'theme',
      'tinymce-custom-colors-forecolor',
      'tinymce-custom-colors-hilitecolor',
      'token',
      'uid',
    ];
    keysToRemove.forEach(key => this.storage.removeItem(key));
    this.loggedIn = false;
    this.currentUser = new User();
    this.currentUserSubject.next(null);
    this.userLoggedOut.next(true);
    this.router.navigateByUrl('/auth');
  }

  /**
   * Fetch user subscription data from the backend
   */
  async fetchUserSubscription(userId: string): Promise<void> {
    const url = `${this.apiUrl}/api/subscription/user/${userId}`;
    const subscription: any = await firstValueFrom(this.http.get(url));
    this.userSubscription.set({
      dbSubscription: subscription.dbSubscription,
      razorpaySubscription: subscription.razorpaySubscription,
    });
  }

  /**
   * Initialize the Auth Service (called in APP_INITIALIZER)
   */
  initAuthService(): void {
    const token = this.storage.getItem('token');
    if (token && !this.isTokenExpired(token)) {
      const decodedUser = this.decodeUserToken(token);
      this.setCurrentUser(decodedUser);
      this.loggedIn = true;
      this.userLoggedIn.next(true);
      this.fetchUserSubscription(decodedUser._id);
    }
  }
}
